import { dev } from "@phosphor/utils";
import { useAtomValue, useStore } from "jotai";
import { useEffect } from "react";
import { Card } from "#scripts/keyboard-demo/components/Card.tsx";
import { CCardList } from "#scripts/keyboard-demo/createKeyboardDemo.ts";
import { UKeydownRootHandler } from "#scripts/keyboard-demo/plugins/CActions.ts";
import type { UID } from "#scripts/vmc/WorldState.ts";
import { useComponent, useUnique } from "#scripts/world-state-demo/world-state-context.ts";

// Guided by @docs/WorldState.md
export function KeyboardDemoApp({ rootUID }: { rootUID: UID }) {
  const store = useStore();
  const rootHandler = useUnique(UKeydownRootHandler);
  const cardList = useComponent(rootUID, CCardList);
  const cardUIDs = useAtomValue(cardList.cardUIDsAtom);

  useEffect(() => {
    store.set(rootHandler.rootUIDAtom, rootUID);
  }, [store, rootHandler, rootUID]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // the handler can be set once to capture all keyboard events
      rootHandler.handler(dev`User keydown`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/components/KeyboardDemoApp.tsx:24:27" }), event);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [rootHandler]);

  return (
    <div data-loc="phosphor-blog/scripts/keyboard-demo/components/KeyboardDemoApp.tsx:31:10" data-name="div" className="keyboard-demo">
      <div data-loc="phosphor-blog/scripts/keyboard-demo/components/KeyboardDemoApp.tsx:32:12" data-name="div" className="demo-cards">
        {cardUIDs.map((uid) => (
          <Card key={uid} uid={uid} />
        ))}
      </div>
      <div data-loc="phosphor-blog/scripts/keyboard-demo/components/KeyboardDemoApp.tsx:37:12" data-name="div" className="demo-help">
        <p>Click a card to focus it, then use:</p>
        <ul>
          <li>↑/↓ to navigate</li>
          <li>X to delete</li>
          <li>D to duplicate</li>
          <li>E to edit</li>
          <li>Tab/Shift+Tab for browser native navigation</li>
        </ul>
      </div>
    </div>
  );
}
